import { FormattedMessage } from 'react-intl';
import React from 'react';
import {
  dateToNotExceedYesterday,
  formatTimestampAsDate,
  getDateAfterDeductFromToday,
  getDateAtEndOfMonth,
  getDateAtEndOfWeek,
  getDateAtEndOfYear,
  getDateAtStartOfMonth,
  getDateAtStartOfWeek,
  getDateAtStartOfYear, getSelectedYear,
} from './dates-utils';
import parseISO from 'date-fns/parseISO';
import { addDays } from 'date-fns';

export const TODAY_FORMATTED = formatTimestampAsDate(new Date());

export const TODAY = {
  label: <FormattedMessage id="date.options.today"
                           defaultMessage="Today" />,
  value: 1,
  option: {
    dateFrom: TODAY_FORMATTED,
    dateTo: TODAY_FORMATTED,
  },
};

export const YESTERDAY = {
  label: <FormattedMessage id="date.options.yesterday"
                           defaultMessage="Yesterday" />,
  value: 2,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAfterDeductFromToday(0, 0, 0, 1)),
      dateTo: formatTimestampAsDate(getDateAfterDeductFromToday(0, 0, 0, 1)),
    },
};

export const THIS_WEEK = {
  label: <FormattedMessage id="date.options.this.week"
                           defaultMessage="This week" />,
  value: 3,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfWeek(new Date())),
      dateTo: formatTimestampAsDate(getDateAtEndOfWeek(new Date())),
    },
};

export const LAST_WEEK = {
  label: <FormattedMessage id="date.options.last.week"
                           defaultMessage="Last week" />,
  value: 4,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfWeek(getDateAfterDeductFromToday(0, 0, 1, 0))),
      dateTo: formatTimestampAsDate(getDateAtEndOfWeek(getDateAfterDeductFromToday(0, 0, 1, 0))),
    },
};

export const LAST_TW0_WEEKS = {
  label: <FormattedMessage id="date.options.last.two.weeks"
                           defaultMessage="Last two weeks" />,
  value: 5,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfWeek(getDateAfterDeductFromToday(0, 0, 1, 0))),
      dateTo: formatTimestampAsDate(getDateAtEndOfWeek(new Date())),
    },
};

export const THIS_MONTH = {
  label: <FormattedMessage id="date.options.this.month"
                           defaultMessage="This month" />,
  value: 6,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(new Date())),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date())),
    },
};

export const LAST_MONTH = {
  label: <FormattedMessage id="date.options.last.month"
                           defaultMessage="Last month" />,
  value: 7,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(getDateAfterDeductFromToday(0, 1, 0, 0))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(getDateAfterDeductFromToday(0, 1, 0, 0))),
    },
};

export const LAST_TWO_MONTHS = {
  label: <FormattedMessage id="date.options.last.two.months"
                           defaultMessage="Last two months" />,
  value: 8,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(getDateAfterDeductFromToday(0, 1, 0, 0))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date())),
    },
};

export const THIS_YEAR = {
  label: <FormattedMessage id="date.options.this.year"
                           defaultMessage="This year" />,
  value: 9,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfYear(new Date())),
      dateTo: formatTimestampAsDate(getDateAtEndOfYear(new Date())),
    },
};

export const LAST_YEAR = {
  label: <FormattedMessage id="date.options.last.year"
                           defaultMessage="Last year" />,
  value: 10,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfYear(getDateAfterDeductFromToday(1, 0, 0, 0))),
      dateTo: formatTimestampAsDate(getDateAtEndOfYear(getDateAfterDeductFromToday(1, 0, 0, 0))),
    },
};

export const LAST_TWO_YEARS = {
  label: <FormattedMessage id="date.options.last.two.years"
                           defaultMessage="Last two years" />,
  value: 11,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfYear(getDateAfterDeductFromToday(1, 0, 0, 0))),
      dateTo: formatTimestampAsDate(getDateAtEndOfYear(new Date())),
    },
};

export const DATE_OPTIONS = [TODAY, YESTERDAY, THIS_WEEK, LAST_WEEK, LAST_TW0_WEEKS, THIS_MONTH, LAST_MONTH, LAST_TWO_MONTHS, THIS_YEAR, LAST_YEAR, LAST_TWO_YEARS];

export const DATE_OPTIONS_STANDARD_REPORT = [TODAY, YESTERDAY, THIS_WEEK, LAST_WEEK, LAST_TW0_WEEKS, THIS_MONTH, LAST_MONTH].map(item => ({
  ...item,
  option: {
    dateFrom: parseISO(item.option.dateFrom),
    dateTo: parseISO(item.option.dateTo),
  },
}));

export const DATE_OPTIONS_STANDARD_REPORT_NEXT_DAY = [TODAY, YESTERDAY, THIS_WEEK, LAST_WEEK, LAST_TW0_WEEKS, THIS_MONTH, LAST_MONTH].map(item => ({
  ...item,
  option: {
    dateFrom: parseISO(item.option.dateFrom),
    dateTo: addDays(parseISO(item.option.dateTo), 1),
  },
}));

export const PERIODIC_WITHOUT_FUTURE_DATES = DATE_OPTIONS
  .map((dateOption) => ({
    ...dateOption,
    option: {
      ...dateOption.option,
      dateTo: dateToNotExceedYesterday(dateOption.option.dateTo),
    },
  }))
  .filter((dateOption) => {
    const currentDateFrom = new Date(dateOption.option?.dateFrom);
    const yesterdayDateFrom = new Date(YESTERDAY.option.dateFrom);
    return !currentDateFrom || currentDateFrom <= yesterdayDateFrom;
  });

export const DAY = {
  label: <FormattedMessage id="time.unit.options.day" defaultMessage="Day" />,
  value: 'Day',
};
export const WEEK = {
  label: <FormattedMessage id="time.unit.options.week" defaultMessage="Week" />,
  value: 'Week',
};
export const MONTH = {
  label: <FormattedMessage id="time.unit.options.month" defaultMessage="Month" />,
  value: 'Month',
};
export const QUARTER = {
  label: <FormattedMessage id="time.unit.options.quarter" defaultMessage="Quarter" />,
  value: 'Quarter',
};
export const YEAR = {
  label: <FormattedMessage id="time.unit.options.year" defaultMessage="Year" />,
  value: 'Year',
};
export const HOUR = {
  label: <FormattedMessage id="time.unit.options.hour" defaultMessage="Hour" />,
  value: 'Hour',
};
export const DAY_AND_HOUR = {
  label: <FormattedMessage id="time.unit.options.day.and.hour" defaultMessage="Day and hour" />,
  value: 'DayAndHour',
};
export const YEAR_AND_MONTH = {
  label: <FormattedMessage id="time.unit.options.year.and.month" defaultMessage="Year and month" />,
  value: 'YearAndMonth',
};

export const TIME_UNIT_OPTIONS = [HOUR, DAY, DAY_AND_HOUR, WEEK, MONTH, QUARTER, YEAR, YEAR_AND_MONTH];

export const DAY_OPTION_2 = {
  label: <FormattedMessage id="time.unit.options.day" defaultMessage="Day" />,
  value: 1,
};
export const WEEK_OPTION_2 = {
  label: <FormattedMessage id="time.unit.options.week" defaultMessage="Week" />,
  value: 2,
};
export const MONTH_OPTION_2 = {
  label: <FormattedMessage id="time.unit.options.month" defaultMessage="Month" />,
  value: 3,
};
export const QUARTER_OPTION_2 = {
  label: <FormattedMessage id="time.unit.options.quarter" defaultMessage="Quarter" />,
  value: 4,
};
export const YEAR_OPTION_2 = {
  label: <FormattedMessage id="time.unit.options.year" defaultMessage="Year" />,
  value: 5,
};
export const HOUR_OPTION_2 = {
  label: <FormattedMessage id="time.unit.options.hour" defaultMessage="Hour" />,
  value: 6,
};
export const HOUR_EACH_DAY_OPTION_2 = {
  label: <FormattedMessage id="time.unit.options.hour.each.day" defaultMessage="Hour each day" />,
  value: 7,
};
export const HALF_HOUR_OPTION_2 = {
  label: <FormattedMessage id="time.unit.options.half.hour" defaultMessage="Half an hour" />,
  value: 9,
};
export const QUARTER_OF_HOUR_OPTION_2 = {
  label: <FormattedMessage id="time.unit.options.quarter.hour" defaultMessage="Quarter of hour" />,
  value: 10,
};

export const TIME_UNIT_OPTIONS_2 = [QUARTER_OF_HOUR_OPTION_2, HALF_HOUR_OPTION_2, HOUR_OPTION_2, DAY_OPTION_2, WEEK_OPTION_2, MONTH_OPTION_2, QUARTER_OPTION_2, YEAR_OPTION_2, HOUR_EACH_DAY_OPTION_2];

export const MONDAY = {
  label: <FormattedMessage id="day.options.monday" defaultMessage="Monday" />,
  value: 1,
};
export const TUESDAY = {
  label: <FormattedMessage id="day.options.tuesday" defaultMessage="Tuesday" />,
  value: 2,
};
export const WEDNESDAY = {
  label: <FormattedMessage id="day.options.wednesday" defaultMessage="Wednesday" />,
  value: 3,
};
export const THURSDAY = {
  label: <FormattedMessage id="day.options.thursday" defaultMessage="Thursday" />,
  value: 4,
};
export const FRIDAY = {
  label: <FormattedMessage id="day.options.friday" defaultMessage="Friday" />,
  value: 5,
};
export const SATURDAY = {
  label: <FormattedMessage id="day.options.saturday" defaultMessage="Saturday" />,
  value: 6,
};
export const SUNDAY = {
  label: <FormattedMessage id="day.options.sunday" defaultMessage="Sunday" />,
  value: 0,
};
export const NOT_SPECIFIED = {
  label: <FormattedMessage id="day.options.not.specified" defaultMessage="Not specified" />,
  value: '- Ej angiven -',
};

export const DAY_OPTIONS = [NOT_SPECIFIED, MONDAY, TUESDAY, WEDNESDAY, THURSDAY, FRIDAY, SATURDAY, SUNDAY];

export const JANUARY = {
  label: <FormattedMessage id="date.options.january"
                           defaultMessage="January" />,
  value: 0,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(new Date(getSelectedYear(0, new Date()), 0, 1))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date(getSelectedYear(0, new Date()), 0, 1))),
    },
};

export const FEBRUARY = {
  label: <FormattedMessage id="date.options.february"
                           defaultMessage="February" />,
  value: 1,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(new Date(getSelectedYear(1, new Date()), 1, 1))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date(getSelectedYear(1, new Date()), 1, 1))),
    },
};

export const MARCH = {
  label: <FormattedMessage id="date.options.march"
                           defaultMessage="March" />,
  value: 2,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(new Date(getSelectedYear(2, new Date()), 2, 1))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date(getSelectedYear(2, new Date()), 2, 1))),
    },
};

export const APRIL = {
  label: <FormattedMessage id="date.options.april"
                           defaultMessage="April" />,
  value: 3,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(new Date(getSelectedYear(3, new Date()), 3, 1))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date(getSelectedYear(3, new Date()), 3, 1))),
    },
};

export const MAY = {
  label: <FormattedMessage id="date.options.may"
                           defaultMessage="May" />,
  value: 4,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(new Date(getSelectedYear(4, new Date()), 4, 1))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date(getSelectedYear(4, new Date()), 4, 1))),
    },
};

export const JUNE = {
  label: <FormattedMessage id="date.options.june"
                           defaultMessage="June" />,
  value: 5,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(new Date(getSelectedYear(5, new Date()), 5, 1))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date(getSelectedYear(5, new Date()), 5, 1))),
    },
};

export const JULY = {
  label: <FormattedMessage id="date.options.july"
                           defaultMessage="July" />,
  value: 6,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(new Date(getSelectedYear(6, new Date()), 6, 1))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date(getSelectedYear(6, new Date()), 6, 1))),
    },
};

export const AUGUST = {
  label: <FormattedMessage id="date.options.august"
                           defaultMessage="August" />,
  value: 7,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(new Date(getSelectedYear(7, new Date()), 7, 1))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date(getSelectedYear(7, new Date()), 7, 1))),
    },
};

export const SEPTEMBER = {
  label: <FormattedMessage id="date.options.september"
                           defaultMessage="September" />,
  value: 8,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(new Date(getSelectedYear(8, new Date()), 8, 1))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date(getSelectedYear(8, new Date()), 8, 1))),
    },
};

export const OCTOBER = {
  label: <FormattedMessage id="date.options.october"
                           defaultMessage="October" />,
  value: 9,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(new Date(getSelectedYear(9, new Date()), 9, 1))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date(getSelectedYear(9, new Date()), 9, 1))),
    },
};

export const NOVEMBER = {
  label: <FormattedMessage id="date.options.november"
                           defaultMessage="November" />,
  value: 10,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(new Date(getSelectedYear(10, new Date()), 10, 1))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date(getSelectedYear(10, new Date()), 10, 1))),
    },
};

export const DECEMBER = {
  label: <FormattedMessage id="date.options.december"
                           defaultMessage="December" />,
  value: 11,
  option:
    {
      dateFrom: formatTimestampAsDate(getDateAtStartOfMonth(new Date(getSelectedYear(11, new Date()), 11, 1))),
      dateTo: formatTimestampAsDate(getDateAtEndOfMonth(new Date(getSelectedYear(11, new Date()), 11, 1))),
    },
};

export const MONTHS_OPTIONS = [JANUARY, FEBRUARY, MARCH, APRIL, MAY, JUNE, JULY, AUGUST, SEPTEMBER, OCTOBER, NOVEMBER, DECEMBER];

export const DATE_HEADERS = ['Datum', 'DateTime', 'Transaktionsdatum', 'Z-rapport datum & tid', 'Date & time'];

export const TEN_MINUTES_IN_MILLISECONDS = 10 * 60 * 1000;